import {APP_INITIALIZER, enableProdMode, importProvidersFrom, provideZoneChangeDetection} from '@angular/core';
import {bootstrapApplication} from '@angular/platform-browser';
import {provideRouter, RouteReuseStrategy} from '@angular/router';
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {environment} from '@env-tech/environment';
import {provideStore, Store} from "@ngrx/store";
import {provideEffects} from "@ngrx/effects";
import {provideStoreDevtools} from "@ngrx/store-devtools";
import {IonicStorageModule} from "@ionic/storage-angular";
import {Drivers} from '@ionic/storage';
import {appStore} from "@core/store/core-app.reducer";
import {HttpClient, HttpClientModule} from '@angular/common/http';
import {DefaultDataServiceConfig, EntityDataModule, EntityMetadataMap, provideEntityData} from "@ngrx/data";
import {provideTranslateService, TranslateLoader} from '@ngx-translate/core';
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import * as routes from "@tech/routes";
import CordovaSQLiteDriver from 'localforage-cordovasqlitedriver';
import {defineCustomElements} from '@ionic/pwa-elements/loader';
import {TechAppComponent} from "@tech/tech-app.component";
import {techRegisterStore} from "@tech/pages/register/store/tech-register.reducer";
import {techIssueStore} from "@tech/pages/issues/store/tech-issue.reducers";
import {attachmentsStore} from "@shared/components/attachments/store/attachments.reducers";
import {AttachmentsApiEffects} from "@shared/components/attachments/store/attachments-api.effects";
import {TechIssueApiEffects} from "@tech/pages/issues/store/tech-issue-api-effects";
import {TechInfoApiEffects} from "@tech/pages/infos/store/tech-info-api.effects";
import {TechRegisterEffects} from "@tech/pages/register/store/tech-register.effects";
import {techInfosStore} from "@tech/pages/infos/store/tech-info.reducers";
import {techInventoryStore} from "@tech/pages/inventory/store/tech-inventory.reducers";
import {TechInventoryApiEffects} from "@tech/pages/inventory/store/tech-inventory-api.effects";
import {registerLocaleData} from "@angular/common";
import localeDe from '@angular/common/locales/de';
import {sharedDocumentStore} from "@shared/components/documents/store/shared-document.reducers";
import {SharedDocumentApiEffects} from "@shared/components/documents/store/shared-document.api-effects";
import {sharedKnowledgeStore} from '@shared/components/knowledge/store/knowledge.reducers';
import {SharedKnowledgeApiEffects} from '@shared/components/knowledge/store/knowledge-api.effects';
import {CoreCustomTranslateHttpLoaderService} from "@core/services/core-custom-translate-http-loader.service";
import {sharedPermissionsStore} from "@shared/stores/permissions/store/shared-permissions.reducer";
import {SharedPermissionsEffects} from "@shared/stores/permissions/store/shared-permissions.effects";
import {sharedChatStore} from "@shared/components/chat/store/shared-chat.reducers";
import {SharedChatApiEffects} from "@shared/components/chat/store/shared-chat.api-effects";
import {sharedNotificationListStore} from "@shared/components/notification-list/store/shared-notification-list.reducers";
import {SharedNotificationListApiEffects} from "@shared/components/notification-list/store/shared-notification-list-api.effects";
import {sharedLoginStore} from "@shared/components/login/store/shared-login.reducer";
import {SharedLoginEffects} from "@shared/components/login/store/shared-login.effects";
import {sharedSolutionStore} from "@shared/components/solution/store/shared-solution.reducer";
import {SharedSolutionEffects} from "@shared/components/solution/store/shared-solution.effects";
import {SharedLoginActions} from "@shared/components/login/store/shared-login.actions-type";
import {techMissionCreateStore} from "@tech/pages/inventory/pages/mission-create/store/tech-mission-create.reducer";
import {TechMissionCreateEffects} from "@tech/pages/inventory/pages/mission-create/store/tech-mission-create.effects";

// currencyPipe it is only include support to en-US
// so we need to register the local de-DE to Angular to allow currencyPipe work also with it
registerLocaleData(localeDe);

if (environment.production) {
  enableProdMode();
}

defineCustomElements(window);

const entityMetadataMap: EntityMetadataMap = {
  Chat: {
    selectId: chat => chat.chatByFormDefinitionId
  }
};

const defaultDataServiceConfig: DefaultDataServiceConfig = {
  root: environment.apiUrl.one
}

export function createTranslateLoader(http: HttpClient): CoreCustomTranslateHttpLoaderService {
  return new CoreCustomTranslateHttpLoaderService(http);
}

export function setBrandInitializer(store: Store): () => void {
  return () => store.dispatch(SharedLoginActions.setBrand({ brand: environment.brand }));
}

bootstrapApplication(TechAppComponent, {
  providers: [
    provideZoneChangeDetection(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: DefaultDataServiceConfig, useValue: defaultDataServiceConfig },
    provideStore({
      app: appStore,
      techRegister: techRegisterStore,
      techInfos: techInfosStore,
      techInventory: techInventoryStore,
      techMissionCreate: techMissionCreateStore,
      sharedKnowledge: sharedKnowledgeStore,
      sharedDocuments: sharedDocumentStore,
      sharedChat: sharedChatStore,
      sharedLogin: sharedLoginStore,
      sharedPermissions: sharedPermissionsStore,
      sharedSolution: sharedSolutionStore,
      issues: techIssueStore,
      attachments: attachmentsStore,
      sharedNotifications: sharedNotificationListStore
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: setBrandInitializer,
      deps: [Store],
      multi: true
    },
    provideEffects([
      TechRegisterEffects,
      TechInfoApiEffects,
      TechInventoryApiEffects,
      TechMissionCreateEffects,
      SharedDocumentApiEffects,
      SharedChatApiEffects,
      SharedKnowledgeApiEffects,
      SharedLoginEffects,
      TechIssueApiEffects,
      AttachmentsApiEffects,
      SharedPermissionsEffects,
      SharedSolutionEffects,
      SharedNotificationListApiEffects
    ]),
    provideStoreDevtools({
      name: 'semco ONE'
    }),
    provideRouter(routes.default),
    provideEntityData({ entityMetadata: entityMetadataMap }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    importProvidersFrom(
      IonicModule.forRoot({
        spinner: 'dots',
        swipeBackEnabled: false,
      }),
      IonicStorageModule.forRoot({
        name: 'one',
        driverOrder: [
          CordovaSQLiteDriver._driver,
          Drivers.IndexedDB,
          Drivers.LocalStorage
        ]
      }),
      HttpClientModule,
      BrowserAnimationsModule,
      EntityDataModule.forRoot({})
    ),
  ]
});
